// The pistol model: a USP-S by DFS studio (Sketchfab, CC-BY-4.0), an untextured model built from
// scratch in Blender. It replaces the Glock in the arm rig (fpsRig.ts): lined up on the Glock's
// muzzle and grip, with the slide on the rig's slide bone (so it recoils) and the barrel and
// silencer on its barrel bone. Slide, frame and silencer take the finish; barrel and sights stay plain.

import * as THREE from 'three';
import { byName, type RigPistol } from './fpsRig';

export const USP_URL = `${import.meta.env.BASE_URL ?? '/'}assets/models/usp-s/usp-s.gltf`;

/** Points on the model, in its own units: bore exit at the slide's front, grip middle, silencer end. */
const BORE = new THREE.Vector3(0.06, 1.09, 0);
const GRIP = new THREE.Vector3(3.24, -0.55, 0);
const SILENCER_END = new THREE.Vector3(-4.02, 1.09, 0);
/** The muzzle points along the model's -X. */
const MODEL_FWD = new THREE.Vector3(-1, 0, 0);
/** Size relative to the Glock (fitted on muzzle-to-grip distance, then tuned by eye). */
const SIZE = 1.0;

type Part = 'slide' | 'frame' | 'barrel' | 'silencer' | 'sight';
const PARTS: Record<string, Part> = {
  Plane: 'frame',
  Plane001: 'slide',
  Plane002: 'slide',
  Plane003: 'sight',
  Plane004: 'frame',
  Cylinder: 'barrel',
  Cylinder001: 'silencer',
};
const BONE: Record<Part, string> = {
  slide: 'Slide',
  sight: 'Slide',
  frame: 'Root',
  barrel: 'Barrel',
  silencer: 'Barrel',
};

/** Muzzle in the rig's barrel bone frame (where the Glock's muzzle flash sits). */
const RIG_MUZZLE = new THREE.Vector3(0, 0.0052, 0.0003);

/** A frame from a muzzle point, the muzzle direction and a grip point (up = from the grip to the bore). */
function frame(muzzle: THREE.Vector3, fwd: THREE.Vector3, grip: THREE.Vector3) {
  const up = muzzle.clone().sub(grip);
  up.addScaledVector(fwd, -up.dot(fwd)).normalize();
  const side = new THREE.Vector3().crossVectors(fwd, up);
  return new THREE.Matrix4().makeBasis(fwd, up, side);
}

/**
 * Swap the rig's Glock for the USP-S. `slideMat` is the finish material (its texture uses the
 * procedural blade's UV layout, so the slide gets matching planar UVs). Returns false if the model
 * didn't load (the Glock stays).
 */
export async function fitUsp(
  p: RigPistol,
  rigScene: THREE.Object3D,
  slideMat: THREE.Material,
): Promise<boolean> {
  try {
    const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
    const gltf = await new GLTFLoader().loadAsync(USP_URL);
    const model = gltf.scene;
    model.updateMatrixWorld(true);
    rigScene.updateMatrixWorld(true);

    // Rig: muzzle, barrel direction, grip.
    const barrel = byName(rigScene, 'Barrel');
    const muzzleRig = barrel.localToWorld(RIG_MUZZLE.clone());
    const fwdRig = byName(rigScene, 'Barrel_end')
      .getWorldPosition(new THREE.Vector3())
      .sub(barrel.getWorldPosition(new THREE.Vector3()))
      .normalize();
    const gripRig = byName(rigScene, 'Magazine').getWorldPosition(new THREE.Vector3());
    const scale = (SIZE * muzzleRig.distanceTo(gripRig)) / BORE.distanceTo(GRIP);
    // Model -> rig world: rotate the model's frame onto the rig's, scale, bore onto the muzzle.
    const rot = frame(muzzleRig, fwdRig, gripRig).multiply(frame(BORE, MODEL_FWD, GRIP).transpose());
    const toRig = new THREE.Matrix4()
      .makeTranslation(muzzleRig.x, muzzleRig.y, muzzleRig.z)
      .multiply(rot)
      .multiply(new THREE.Matrix4().makeScale(scale, scale, scale))
      .multiply(new THREE.Matrix4().makeTranslation(-BORE.x, -BORE.y, -BORE.z));

    const darkMat = new THREE.MeshPhongMaterial({ color: 0x0f1012, specular: 0x50545c, shininess: 45 });
    const steelMat = new THREE.MeshPhongMaterial({ color: 0x6c7178, specular: 0xffffff, shininess: 90 });
    const meshes: THREE.Mesh[] = [];
    model.traverse((o) => {
      if (o instanceof THREE.Mesh) meshes.push(o);
    });
    let silencer: THREE.Mesh | null = null;
    for (const mesh of meshes) {
      const part = PARTS[mesh.name.replace(/_\d+$/, '')] ?? 'frame';
      const geo = mesh.geometry.clone();
      // The finish goes on the slide, the frame (grip) and the silencer; barrel and sights stay plain.
      const finished = part === 'slide' || part === 'frame' || part === 'silencer';
      if (finished) slideUvs(geo, mesh.matrixWorld);
      const out = new THREE.Mesh(geo, finished ? slideMat : part === 'barrel' ? steelMat : darkMat);
      out.frustumCulled = false;
      const bone = byName(rigScene, BONE[part]);
      bone.matrixWorld
        .clone()
        .invert()
        .multiply(toRig)
        .multiply(mesh.matrixWorld)
        .decompose(out.position, out.quaternion, out.scale);
      bone.add(out);
      if (part === 'silencer') silencer = out;
    }

    // Hide the Glock. Its magazine only shows while reloading (the animation swaps it); at rest it
    // would poke out under the USP's grip.
    const mag: THREE.Object3D[] = [];
    byName(rigScene, 'Glock19').traverse((o) => {
      if (!(o instanceof THREE.Mesh)) return;
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      o.visible = false;
      if (mats.some((m) => /Magazine|Bullet/.test(m.name))) mag.push(o);
    });
    p.showMagazine = (on: boolean) => {
      for (const o of mag) o.visible = on;
    };
    p.silencer.visible = false;
    p.silencer = silencer ?? p.silencer;

    // Muzzle flash at the silencer's end, or at the slide's front without it.
    const inBarrel = (v: THREE.Vector3) => barrel.worldToLocal(v.clone().applyMatrix4(toRig));
    const withSil = inBarrel(SILENCER_END);
    const without = inBarrel(BORE);
    p.setSilenced = (on: boolean) => {
      if (silencer) silencer.visible = on;
      p.muzzle.position.copy(on ? withSil : without);
    };
    return true;
  } catch {
    return false;
  }
}

/** Side-on planar UVs for the slide, in the finish texture's layout (muzzle end at the left). */
function slideUvs(geo: THREE.BufferGeometry, m: THREE.Matrix4) {
  const pos = geo.getAttribute('position');
  const v = new THREE.Vector3();
  const pts: number[] = [];
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i).applyMatrix4(m);
    pts.push(v.x, v.y);
    minX = Math.min(minX, v.x);
    maxX = Math.max(maxX, v.x);
    minY = Math.min(minY, v.y);
    maxY = Math.max(maxY, v.y);
  }
  const uv = new Float32Array(pos.count * 2);
  for (let i = 0; i < pos.count; i++) {
    uv[i * 2] = 0.9 + (pts[i * 2] - minX) / (maxX - minX);
    uv[i * 2 + 1] = -0.6 + (0.85 * (pts[i * 2 + 1] - minY)) / (maxY - minY);
  }
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
}
